import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../config.js';
import { IoShareSocial, IoCopyOutline, IoTrashOutline, IoCalendarOutline, IoLocationOutline, IoBriefcaseOutline, IoBusinessOutline, IoHomeOutline, IoStorefrontOutline, IoSearchOutline } from 'react-icons/io5';
import { MdAttachMoney, MdMeetingRoom, MdDirectionsCar, MdSquareFoot, MdLandscape, MdSettings, MdHomeWork, MdHistory, MdSell } from 'react-icons/md';
import { BsMapFill, BsClipboard2Check, BsStars, BsHouseDoor } from 'react-icons/bs';
import { FaStar, FaGift, FaGem, FaTools, FaBuilding, FaHome, FaBriefcase } from 'react-icons/fa';

const AgentDashboard = ({ userToken }) => {
    const [valuaciones, setValuaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0
    });
    
    // Filters
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };
    
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };
    
    const [startDate, setStartDate] = useState(getTodayDate());
    const [endDate, setEndDate] = useState(getTomorrowDate());
    const [operationFilter, setOperationFilter] = useState('all');
    const [propertyFilter, setPropertyFilter] = useState('all');
    
    // Modal state
    const [selectedValuacion, setSelectedValuacion] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const openModal = (valuacion) => {
        setSelectedValuacion(valuacion);
        setIsModalOpen(true);
    };
    
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedValuacion(null);
    };
    
    const fetchValuaciones = async (page = 1) => {
        try {
            setLoading(true);
            
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20'
            });
            
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (operationFilter !== 'all') params.append('operation', operationFilter);
            if (propertyFilter !== 'all') params.append('property', propertyFilter);
            
            const response = await axios.get(
                `${apiBaseUrl}/api/agent/valuaciones?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${userToken}`
                    }
                }
            );
            
            setValuaciones(response.data.valuaciones || []);
            setPagination(response.data.pagination || {});
        } catch (error) {
            console.error('Error fetching agent valuaciones:', error);
            setValuaciones([]);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (userToken) {
            fetchValuaciones();
        }
    }, [userToken, startDate, endDate, operationFilter, propertyFilter]);
    
    const handleNextPage = () => {
        if (pagination.hasNextPage) {
            fetchValuaciones(pagination.currentPage + 1);
        }
    };
    
    const handlePrevPage = () => {
        if (pagination.hasPrevPage) {
            fetchValuaciones(pagination.currentPage - 1);
        }
    };
    
    return (
        <div style={{ paddingRight: '20px', paddingLeft: '20px' }}>
            {/* Filters Section */}
            <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                marginBottom: '20px'
            }}>
                <h3 style={{ fontSize: '18px', marginBottom: '20px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <IoSearchOutline size={20} />
                    Filtros
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {/* Date From */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d', fontWeight: '500' }}>
                            Desde
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    
                    {/* Date To */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d', fontWeight: '500' }}>
                            Hasta
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    
                    {/* Operation Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d', fontWeight: '500' }}>
                            Operación
                        </label>
                        <select
                            value={operationFilter}
                            onChange={(e) => setOperationFilter(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                                background: 'white'
                            }}
                        >
                            <option value="all">Todas</option>
                            <option value="venta">Venta</option>
                            <option value="alquiler">Alquiler</option>
                        </select>
                    </div>
                    
                    {/* Property Type Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d', fontWeight: '500' }}>
                            Tipo de Propiedad
                        </label>
                        <select
                            value={propertyFilter}
                            onChange={(e) => setPropertyFilter(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                                background: 'white'
                            }}
                        >
                            <option value="all">Todas</option>
                            <option value="departamento">Departamento</option>
                            <option value="casa">Casa</option>
                            <option value="oficina">Oficina</option>
                            <option value="local">Local Comercial</option>
                            <option value="cocheras">Cocheras</option>
                        </select>
                    </div>
                </div>
            </div>
            
            {/* Valuaciones Table */}
            <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', color: '#2c3e50', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <BsClipboard2Check size={20} />
                        Mis Valuaciones ({pagination.totalCount || 0})
                    </h3>
                </div>
                
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                        Cargando...
                    </div>
                ) : valuaciones.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                        No hay valuaciones disponibles
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#495057', textTransform: 'uppercase' }}>
                                            Fecha
                                        </th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#495057', textTransform: 'uppercase' }}>
                                            Dirección
                                        </th>
                                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#495057', textTransform: 'uppercase' }}>
                                            Operación
                                        </th>
                                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#495057', textTransform: 'uppercase' }}>
                                            Tipo
                                        </th>
                                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#495057', textTransform: 'uppercase' }}>
                                            Rango de Precio
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {valuaciones.map((valuacion) => (
                                        <tr 
                                            key={valuacion._id} 
                                            onClick={() => openModal(valuacion)}
                                            style={{ 
                                                borderBottom: '1px solid #e9ecef',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ padding: '12px', fontSize: '13px', color: '#495057' }}>
                                                {new Date(valuacion.timestamp).toLocaleString('es-AR', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                            <td style={{ padding: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={valuacion.address}>
                                                {valuacion.address}
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: valuacion.operation === 'alquiler' ? '#e3f2fd' : '#f3e5f5',
                                                    color: valuacion.operation === 'alquiler' ? '#1976d2' : '#7b1fa2',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                }}>
                                                    {valuacion.operation === 'alquiler' ? (
                                                        <><BsHouseDoor size={14} /> Alquiler</>
                                                    ) : (
                                                        <><MdSell size={14} /> Venta</>
                                                    )}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#495057' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                    {valuacion.property === 'departamento' && <><FaBuilding size={14} /> Departamento</>}
                                                    {valuacion.property === 'casa' && <><FaHome size={14} /> Casa</>}
                                                    {valuacion.property === 'oficina' && <><FaBriefcase size={14} /> Oficina</>}
                                                    {valuacion.property === 'local' && <><IoStorefrontOutline size={16} /> Local</>}
                                                    {valuacion.property === 'cocheras' && <><MdDirectionsCar size={16} /> Cocheras</>}
                                                    {!['departamento', 'casa', 'oficina', 'local', 'cocheras'].includes(valuacion.property) && valuacion.property}
                                                </span>
                                            </td>
                                            <td style={{ 
                                                padding: '12px', 
                                                textAlign: 'center', 
                                                fontWeight: '600', 
                                                color: '#27ae60',
                                                fontSize: '13px'
                                            }}>
                                                {valuacion.priceRange}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div style={{
                                marginTop: '20px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                <button
                                    onClick={handlePrevPage}
                                    disabled={!pagination.hasPrevPage}
                                    style={{
                                        padding: '8px 16px',
                                        background: pagination.hasPrevPage ? '#667eea' : '#e9ecef',
                                        color: pagination.hasPrevPage ? 'white' : '#adb5bd',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: pagination.hasPrevPage ? 'pointer' : 'not-allowed',
                                        fontSize: '14px'
                                    }}
                                >
                                    ← Anterior
                                </button>
                                
                                <span style={{ fontSize: '14px', color: '#495057' }}>
                                    Página {pagination.currentPage} de {pagination.totalPages}
                                </span>
                                
                                <button
                                    onClick={handleNextPage}
                                    disabled={!pagination.hasNextPage}
                                    style={{
                                        padding: '8px 16px',
                                        background: pagination.hasNextPage ? '#667eea' : '#e9ecef',
                                        color: pagination.hasNextPage ? 'white' : '#adb5bd',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                                        fontSize: '14px'
                                    }}
                                >
                                    Siguiente →
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Valuacion Detail Modal */}
            {isModalOpen && selectedValuacion && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '40px 10px',
                        overflowY: 'auto'
                    }}
                    onClick={closeModal}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            maxWidth: '700px',
                            width: '100%',
                            maxHeight: '70vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                            position: 'relative',
                            overflow: 'hidden',
                            marginBottom: '40px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white',
                            padding: '25px 30px',
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            position: 'relative',
                            flexShrink: 0
                        }}>
                            <h2 style={{ 
                                margin: 0, 
                                fontSize: '24px', 
                                fontWeight: '700',
                                paddingRight: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <BsClipboard2Check size={28} />
                                Detalles de Valuación
                            </h2>
                            <button
                                onClick={closeModal}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                            >
                                ×
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div style={{ 
                            padding: '30px 30px 60px 30px',
                            overflowY: 'auto',
                            flex: 1,
                            WebkitOverflowScrolling: 'touch'
                        }}>
                            {/* Date and Time */}
                            <div style={{ 
                                marginBottom: '25px',
                                paddingBottom: '20px',
                                borderBottom: '2px solid #e9ecef'
                            }}>
                                <div style={{ 
                                    fontSize: '14px', 
                                    color: '#7f8c8d',
                                    marginBottom: '5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <IoCalendarOutline size={16} />
                                    Fecha y Hora
                                </div>
                                <div style={{ fontSize: '16px', color: '#2c3e50', fontWeight: '600' }}>
                                    {new Date(selectedValuacion.timestamp).toLocaleString('es-AR', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                            
                            {/* Property Details */}
                            <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '20px',
                                marginBottom: '25px'
                            }}>
                                {/* Address */}
                                <div style={{ 
                                    background: '#f8f9fa',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    gridColumn: '1 / -1'
                                }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        color: '#7f8c8d',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <IoLocationOutline size={16} />
                                        Dirección
                                    </div>
                                    <div style={{ 
                                        fontSize: '18px', 
                                        color: '#2c3e50', 
                                        fontWeight: '600',
                                        wordBreak: 'break-word'
                                    }}>
                                        {selectedValuacion.address}
                                    </div>
                                </div>
                                
                                {/* Operation */}
                                <div style={{ 
                                    background: '#f8f9fa',
                                    padding: '20px',
                                    borderRadius: '12px'
                                }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        color: '#7f8c8d',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <IoBriefcaseOutline size={16} />
                                        Operación
                                    </div>
                                    <div>
                                        <span style={{
                                            padding: '6px 16px',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            backgroundColor: selectedValuacion.operation === 'alquiler' ? '#e3f2fd' : '#f3e5f5',
                                            color: selectedValuacion.operation === 'alquiler' ? '#1976d2' : '#7b1fa2',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            {selectedValuacion.operation === 'alquiler' ? (
                                                <><BsHouseDoor size={16} /> Alquiler</>
                                            ) : (
                                                <><MdSell size={16} /> Venta</>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Property Type */}
                                <div style={{ 
                                    background: '#f8f9fa',
                                    padding: '20px',
                                    borderRadius: '12px'
                                }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        color: '#7f8c8d',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <IoBusinessOutline size={16} />
                                        Tipo de Propiedad
                                    </div>
                                    <div style={{
                                        fontSize: '16px',
                                        color: '#2c3e50',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        {selectedValuacion.property === 'departamento' && <><FaBuilding size={16} /> Departamento</>}
                                        {selectedValuacion.property === 'casa' && <><FaHome size={16} /> Casa</>}
                                        {selectedValuacion.property === 'oficina' && <><FaBriefcase size={16} /> Oficina</>}
                                        {selectedValuacion.property === 'local' && <><IoStorefrontOutline size={18} /> Local</>}
                                        {selectedValuacion.property === 'cocheras' && <><MdDirectionsCar size={18} /> Cocheras</>}
                                        {!['departamento', 'casa', 'oficina', 'local', 'cocheras'].includes(selectedValuacion.property) && selectedValuacion.property}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Price Range - Highlighted */}
                            <div style={{ 
                                background: 'linear-gradient(135deg, #d4edda, #c3e6cb)',
                                padding: '25px',
                                borderRadius: '12px',
                                marginBottom: '25px',
                                border: '2px solid #28a745'
                            }}>
                                <div style={{ 
                                    fontSize: '14px', 
                                    color: '#155724',
                                    marginBottom: '8px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <MdAttachMoney size={18} />
                                    Rango de Precio
                                </div>
                                <div style={{ 
                                    fontSize: '24px', 
                                    color: '#155724', 
                                    fontWeight: '700'
                                }}>
                                    {selectedValuacion.priceRange}
                                </div>
                            </div>
                            
                            {/* Zone */}
                            {selectedValuacion.zone && selectedValuacion.zone !== 'N/A' && (
                                <div style={{ 
                                    background: '#f8f9fa',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        color: '#7f8c8d',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <BsMapFill size={14} />
                                        Zona
                                    </div>
                                    <div style={{ 
                                        fontSize: '16px', 
                                        color: '#2c3e50', 
                                        fontWeight: '600'
                                    }}>
                                        {selectedValuacion.zone}
                                    </div>
                                </div>
                            )}
                            
                            {/* Valuation Type Badges */}
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                {selectedValuacion.valuationType && (
                                    <span style={{
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        backgroundColor: selectedValuacion.valuationType === 'advanced' ? '#fff3cd' : '#d1ecf1',
                                        color: selectedValuacion.valuationType === 'advanced' ? '#856404' : '#0c5460',
                                        border: selectedValuacion.valuationType === 'advanced' ? '1px solid #ffc107' : '1px solid #17a2b8'
                                    }}>
                                        {selectedValuacion.valuationType === 'advanced' ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <FaStar /> Valuación Avanzada
                                            </span>
                                        ) : (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <BsClipboard2Check /> Valuación Básica
                                            </span>
                                        )}
                                    </span>
                                )}
                                {/* Premium/Free Badge - Show if premium valuation */}
                                {selectedValuacion.motive === 'premium_valuation' && (
                                    <span style={{
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        backgroundColor: selectedValuacion.agentId ? '#d4edda' : '#e7e7e7',
                                        color: selectedValuacion.agentId ? '#155724' : '#495057',
                                        border: selectedValuacion.agentId ? '1px solid #c3e6cb' : '1px solid #d1d1d1'
                                    }}>
                                        {selectedValuacion.agentId ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <FaGift /> Crédito Gratis
                                            </span>
                                        ) : (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <FaGem /> Premium Pago
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                            
                            {/* Property Details - Show if available */}
                            {(selectedValuacion.amb || selectedValuacion.garage || selectedValuacion.sc || selectedValuacion.st || selectedValuacion.sd || selectedValuacion.sterre) && (
                                <div style={{ marginBottom: '25px' }}>
                                    <h4 style={{ 
                                        fontSize: '16px', 
                                        color: '#2c3e50', 
                                        marginBottom: '15px',
                                        fontWeight: '700',
                                        borderBottom: '2px solid #e9ecef',
                                        paddingBottom: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <MdHomeWork size={20} />
                                        Detalles de la Propiedad
                                    </h4>
                                    <div style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                        gap: '15px'
                                    }}>
                                        {selectedValuacion.amb && (
                                            <div style={{ 
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderRadius: '8px'
                                            }}>
                                                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <MdMeetingRoom size={14} />
                                                    Ambientes
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>
                                                    {selectedValuacion.amb}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedValuacion.garage && (
                                            <div style={{ 
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderRadius: '8px'
                                            }}>
                                                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <MdDirectionsCar size={14} />
                                                    Cocheras
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>
                                                    {selectedValuacion.garage}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedValuacion.sc && (
                                            <div style={{ 
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderRadius: '8px'
                                            }}>
                                                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <MdSquareFoot size={14} />
                                                    Sup. Cubierta
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>
                                                    {selectedValuacion.sc} m²
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedValuacion.st && (
                                            <div style={{ 
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderRadius: '8px'
                                            }}>
                                                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <MdSquareFoot size={14} />
                                                    Sup. Total
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>
                                                    {selectedValuacion.st} m²
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedValuacion.sd && (
                                            <div style={{ 
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderRadius: '8px'
                                            }}>
                                                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <IoHomeOutline size={14} />
                                                    Sup. Descubierta
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>
                                                    {selectedValuacion.sd} m²
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedValuacion.sterre && (
                                            <div style={{ 
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderRadius: '8px'
                                            }}>
                                                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <MdLandscape size={14} />
                                                    Sup. Terreno
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>
                                                    {selectedValuacion.sterre} m²
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Condition and Age */}
                            {(selectedValuacion.condition || selectedValuacion.antiguedad) && (
                                <div style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '15px',
                                    marginBottom: '25px'
                                }}>
                                    {selectedValuacion.condition && (
                                        <div style={{ 
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '12px'
                                        }}>
                                            <div style={{ 
                                                fontSize: '14px', 
                                                color: '#7f8c8d',
                                                marginBottom: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <MdSettings size={16} />
                                                Condición
                                            </div>
                                            <div style={{ 
                                                fontSize: '16px', 
                                                color: '#2c3e50', 
                                                fontWeight: '600'
                                            }}>
                                                {selectedValuacion.condition}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {selectedValuacion.antiguedad && (
                                        <div style={{ 
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '12px'
                                        }}>
                                            <div style={{
                                                fontSize: '14px',
                                                color: '#7f8c8d',
                                                marginBottom: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <MdHistory size={16} />
                                                Antigüedad
                                            </div>
                                            <div style={{ 
                                                fontSize: '16px', 
                                                color: '#2c3e50', 
                                                fontWeight: '600'
                                            }}>
                                                {selectedValuacion.antiguedad} años
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Amenities */}
                            {(selectedValuacion.barbecue || selectedValuacion.pool || selectedValuacion.sum) && (
                                <div style={{ marginBottom: '25px' }}>
                                    <h4 style={{ 
                                        fontSize: '16px', 
                                        color: '#2c3e50', 
                                        marginBottom: '15px',
                                        fontWeight: '700',
                                        borderBottom: '2px solid #e9ecef',
                                        paddingBottom: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <BsStars size={18} />
                                        Comodidades
                                    </h4>
                                    <div style={{ 
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        {selectedValuacion.barbecue && (
                                            <span style={{
                                                padding: '8px 16px',
                                                background: '#d4edda',
                                                color: '#155724',
                                                borderRadius: '20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                border: '1px solid #c3e6cb'
                                            }}>
                                                🍖 Parrilla
                                            </span>
                                        )}
                                        {selectedValuacion.pool && (
                                            <span style={{
                                                padding: '8px 16px',
                                                background: '#d1ecf1',
                                                color: '#0c5460',
                                                borderRadius: '20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                border: '1px solid #bee5eb'
                                            }}>
                                                🏊 Piscina
                                            </span>
                                        )}
                                        {selectedValuacion.sum && (
                                            <span style={{
                                                padding: '8px 16px',
                                                background: '#fff3cd',
                                                color: '#856404',
                                                borderRadius: '20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                border: '1px solid #ffeaa7'
                                            }}>
                                                🏖️ Quincho/SUM
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Modal Footer */}
                        <div style={{
                            padding: '20px 30px',
                            background: '#f8f9fa',
                            borderBottomLeftRadius: '16px',
                            borderBottomRightRadius: '16px',
                            borderTop: '1px solid #e9ecef',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexShrink: 0,
                            gap: '10px',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ display: 'flex', gap: '10px', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                {/* Delete Button */}
                                <button
                                    onClick={async () => {
                                        if (window.confirm('¿Estás seguro que querés borrar esta valuación de tu historial?')) {
                                            try {
                                                await axios.delete(
                                                    `${apiBaseUrl}/api/agent/valuaciones/${selectedValuacion._id}`,
                                                    {
                                                        headers: {
                                                            'Authorization': `Bearer ${userToken}`
                                                        }
                                                    }
                                                );
                                                alert('✅ Valuación borrada exitosamente');
                                                closeModal();
                                                // Refresh the list
                                                fetchValuaciones(pagination.currentPage);
                                            } catch (err) {
                                                console.error('Error hiding valuacion:', err);
                                                alert('Error al borrar la valuación');
                                            }
                                        }
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#c82333'}
                                    onMouseLeave={(e) => e.target.style.background = '#dc3545'}
                                >
                                    <IoTrashOutline size={18} />
                                    Borrar
                                </button>
                                <button
                                    onClick={() => {
                                        // Create shareable text
                                        const shareText = `📋 VALUACIÓN INMOBILIARIA - ValuacionesYA

📍 Dirección: ${selectedValuacion.address}
🗺️ Zona: ${selectedValuacion.zone || 'N/A'}
📅 Fecha: ${new Date(selectedValuacion.timestamp).toLocaleDateString('es-AR')}

🏢 Tipo: ${selectedValuacion.property === 'departamento' ? 'Departamento' : 
           selectedValuacion.property === 'casa' ? 'Casa' : 
           selectedValuacion.property === 'oficina' ? 'Oficina' : 
           selectedValuacion.property === 'local' ? 'Local Comercial' : 
           selectedValuacion.property === 'cocheras' ? 'Cocheras' : selectedValuacion.property}
💼 Operación: ${selectedValuacion.operation === 'alquiler' ? 'Alquiler' : 'Venta'}

💵 RANGO DE PRECIO: ${selectedValuacion.priceRange}
${selectedValuacion.amb ? `\n🚪 Ambientes: ${selectedValuacion.amb}` : ''}${selectedValuacion.garage ? `\n🚗 Cocheras: ${selectedValuacion.garage}` : ''}${selectedValuacion.sc ? `\n📐 Superficie Cubierta: ${selectedValuacion.sc} m²` : ''}${selectedValuacion.st ? `\n📏 Superficie Total: ${selectedValuacion.st} m²` : ''}${selectedValuacion.condition ? `\n⚙️ Condición: ${selectedValuacion.condition}` : ''}${selectedValuacion.antiguedad ? `\n📅 Antigüedad: ${selectedValuacion.antiguedad} años` : ''}

✨ Valuación ${selectedValuacion.valuationType === 'advanced' ? 'Avanzada' : 'Básica'}

---
Valuación profesional realizada por ValuacionesYA
www.valuacionesya.com`;

                                        // Use Web Share API
                                        if (navigator.share) {
                                            navigator.share({
                                                title: 'Valuación Inmobiliaria',
                                                text: shareText
                                            }).catch(err => {
                                                console.log('Share cancelled or failed:', err);
                                            });
                                        } else {
                                            alert('La función de compartir no está disponible en este navegador');
                                        }
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#218838'}
                                    onMouseLeave={(e) => e.target.style.background = '#28a745'}
                                >
                                    <IoShareSocial size={18} />
                                    Compartir
                                </button>
                                <button
                                    onClick={() => {
                                        // Create shareable text (same as share)
                                        const shareText = `📋 VALUACIÓN INMOBILIARIA - ValuacionesYA

📍 Dirección: ${selectedValuacion.address}
🗺️ Zona: ${selectedValuacion.zone || 'N/A'}
📅 Fecha: ${new Date(selectedValuacion.timestamp).toLocaleDateString('es-AR')}

🏢 Tipo: ${selectedValuacion.property === 'departamento' ? 'Departamento' : 
           selectedValuacion.property === 'casa' ? 'Casa' : 
           selectedValuacion.property === 'oficina' ? 'Oficina' : 
           selectedValuacion.property === 'local' ? 'Local Comercial' : 
           selectedValuacion.property === 'cocheras' ? 'Cocheras' : selectedValuacion.property}
💼 Operación: ${selectedValuacion.operation === 'alquiler' ? 'Alquiler' : 'Venta'}

💵 RANGO DE PRECIO: ${selectedValuacion.priceRange}
${selectedValuacion.amb ? `\n🚪 Ambientes: ${selectedValuacion.amb}` : ''}${selectedValuacion.garage ? `\n🚗 Cocheras: ${selectedValuacion.garage}` : ''}${selectedValuacion.sc ? `\n📐 Superficie Cubierta: ${selectedValuacion.sc} m²` : ''}${selectedValuacion.st ? `\n📏 Superficie Total: ${selectedValuacion.st} m²` : ''}${selectedValuacion.condition ? `\n⚙️ Condición: ${selectedValuacion.condition}` : ''}${selectedValuacion.antiguedad ? `\n📅 Antigüedad: ${selectedValuacion.antiguedad} años` : ''}

✨ Valuación ${selectedValuacion.valuationType === 'advanced' ? 'Avanzada' : 'Básica'}

---
Valuación profesional realizada por ValuacionesYA
www.valuacionesya.com`;

                                        // Copy to clipboard
                                        navigator.clipboard.writeText(shareText).then(() => {
                                            alert('✅ Valuación copiada al portapapeles');
                                        }).catch(err => {
                                            console.error('Error copying to clipboard:', err);
                                            alert('Error al copiar la valuación');
                                        });
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        background: '#17a2b8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#138496'}
                                    onMouseLeave={(e) => e.target.style.background = '#17a2b8'}
                                >
                                    <IoCopyOutline size={18} />
                                    Copiar
                                </button>
                            </div>
                            <button
                                onClick={closeModal}
                                style={{
                                    padding: '12px 30px',
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#5a6fd8'}
                                onMouseLeave={(e) => e.target.style.background = '#667eea'}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentDashboard;


